// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { Call, CallAgent, GroupCallLocator, TeamsMeetingLinkLocator, PermissionConstraints } from "@azure/communication-calling"
import {
  CommunicationUserIdentifier,
  AzureCommunicationTokenCredential,  
} from "@azure/communication-common"
import {
  CallAdapter,
  CallAdapterState,
  FluentThemeProvider,
  DEFAULT_COMPONENT_ICONS,
  CallClientProvider,
  CallAgentProvider,
  CallProvider,
  createAzureCommunicationCallAdapter,
  JoinCallOptions,
  useAzureCommunicationCallAdapter,
} from "@azure/communication-react"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useSwitchableFluentTheme } from "../theming/SwitchableFluentThemeProvider"
import { createAutoRefreshingCredential } from "../utils/credential";
import MobileDetect from "mobile-detect"
import CallingComponents from "./CallComponentsStateful"
import { MessageBar, MessageBarType, registerIcons } from "@fluentui/react"
import Lobby from '../components/Lobby'
import { useId, useBoolean } from "@fluentui/react-hooks";
import { Dialog, DialogType, IDialogContentProps } from "@fluentui/react/lib/Dialog";
import { ContextualMenu } from "@fluentui/react/lib/ContextualMenu";
import { IButtonProps, PrimaryButton } from "@fluentui/react/lib/Button";
import { useHistory } from "react-router-dom";
import { FontSizes, FontWeights } from "@fluentui/theme";

const detectMobileSession = (): boolean =>
  !!new MobileDetect(window.navigator.userAgent).mobile()

export interface CallScreenProps {
  token: string
  userId: CommunicationUserIdentifier
  groupId: string
  callLocator: GroupCallLocator | TeamsMeetingLinkLocator
  displayName: string
  webAppTitle: string
  onCallEnded: () => void
}

export const CallScreen = (props: CallScreenProps): JSX.Element => {
  console.log("twice?");
  const history = useHistory();
  registerIcons({ icons: DEFAULT_COMPONENT_ICONS })
  const {
    token,
    userId,
    callLocator,
    displayName,
    webAppTitle,
    onCallEnded,
  } = props
  //const [adapter, setAdapter] = useState<CallAdapter>();
  const [hideDialog, setHideDialog] = useState<boolean>(true);
  const callIdRef = useRef<string>();
  const adapterRef = useRef<CallAdapter>();
  const [call, setCall] = useState<Call>();
  const [dismissDialog, { toggle: toggleDismissDialog }] =
    useBoolean(true);
  const labelId: string = useId("dialogLabel");
  const subTextId: string = useId("subTextLabel");
  const dialogStyles = { main: { maxWidth: 450 } };
  const [isDraggable] = useBoolean(false);
  const [recordingMessage, setRecordingMessage] = useState("");
  const ref = useRef(false);

  const dragOptions = {
    moveMenuItemText: "Move",
    closeMenuItemText: "Close",
    menu: ContextualMenu,
    keepInBounds: true,
  };

  const modalProps = React.useMemo(
    () => ({
      titleAriaId: labelId,
      subtitleAriaId: subTextId,
      isBlocking: false,
      styles: dialogStyles,
      dragOptions: isDraggable ? dragOptions : undefined,
    }),
    [isDraggable, labelId, subTextId]
  );
  
  const btnProps: IButtonProps = {
    disabled: ref.current === true  
  }

  const dismissalDialogProps: IDialogContentProps = {
    type: DialogType.normal,
    title : "Exam finished",
    subText: "Exam has finished.",
    topButtonsProps: [btnProps]
  };

  const credential = useMemo(() => new AzureCommunicationTokenCredential(token), [token]);  
  const afterCreate = async (adapter: CallAdapter): Promise<CallAdapter> => {
    if (adapter){
      // Events
      adapter.on('participantsLeft', () => {

      });
      adapter.on('error', (e) => {
        // Error is already acted upon by the Call composite, but the surrounding application could
        // add top-level error handling logic here (e.g. reporting telemetry).
        console.log('Adapter error event:', e);
      });
      adapter.onStateChange((state: CallAdapterState) => {
        const pageTitle = convertPageStateToString(state);
        document.title = `${pageTitle} - ${webAppTitle}`;
        callIdRef.current = state?.call?.id;            
        if(state.page == "removedFromCall") {
          if(hideDialog)  {
            setHideDialog(false);            
          }            
        }       

        if(!ref.current && state.call?.recording.isRecordingActive) {
          setRecordingMessage("Recording started");
          ref.current = true;
        }
        if(!state.call?.recording.isRecordingActive) {
          if(ref.current) {
            setRecordingMessage("Recording has ended and saved !");
            ref.current = false;
          }
        }
      });      

      // Devices
      let constrain:PermissionConstraints = {audio:true, video:true}; 
      adapter.askDevicePermission(constrain).then((permission) => {console.log("Permissions", permission)});
      adapter.queryCameras().then((cameras) => {console.log("Cameras", cameras)});

      // Join call
      let joinCallOptions: JoinCallOptions ={ microphoneOn: false, cameraOn: false}
      setCall(adapter.joinCall(joinCallOptions));        
    }

    return adapter;
  };
  
  const adapter = useAzureCommunicationCallAdapter({
    userId,
    displayName,
    credential,
    locator: callLocator,
  },
  afterCreate);

  // useEffect(() => {
  //   (async () => {
  //     const adapter = await createAzureCommunicationCallAdapter({
  //       userId,
  //       displayName,
  //       credential: createAutoRefreshingCredential({ communicationUserId : userId.communicationUserId }, token),
  //       locator: callLocator
  //     });

  //     adapter.on('participantsLeft', () => {

  //     });

  //     adapter.on('error', (e) => {
  //       // Error is already acted upon by the Call composite, but the surrounding application could
  //       // add top-level error handling logic here (e.g. reporting telemetry).
  //       console.log('Adapter error event:', e);
  //     });
  //     adapter.onStateChange((state: CallAdapterState) => {
  //       const pageTitle = convertPageStateToString(state);
  //       document.title = `${pageTitle} - ${webAppTitle}`;
  //       callIdRef.current = state?.call?.id;            
  //       if(state.page == "removedFromCall") {
  //         if(hideDialog)  {
  //           setHideDialog(false);            
  //         }            
  //       }       

  //       if(!ref.current && state.call?.recording.isRecordingActive) {
  //         setRecordingMessage("Recording started");
  //         ref.current = true;
  //       }
  //       if(!state.call?.recording.isRecordingActive) {
  //         if(ref.current) {
  //           setRecordingMessage("Recording has ended and saved !");
  //           ref.current = false;
  //         }
  //       }
  //     });      
  //     setAdapter(adapter);
  //     adapterRef.current = adapter;  

  //     let joinCallOptions: JoinCallOptions ={ microphoneOn: true, cameraOn: true}
  //     setCall(adapter.joinCall(joinCallOptions));     
  //   })();

  //   return () => {
  //     adapterRef?.current?.dispose();
  //   };
  // }, [callLocator, displayName, token, userId, onCallEnded]);

  return (
    <>
      <FluentThemeProvider>
        {
            recordingMessage ? 
              <MessageBar
              style={{
                fontSize: FontSizes.mediumPlus,
                fontWeight: FontWeights.bold,
              }}
              messageBarType={MessageBarType.warning}
              isMultiline={false}
              dismissButtonAriaLabel="Close"
            >
              { recordingMessage }
            </MessageBar>
            : null
          }
        {adapter && (
          <CallClientProvider callClient={(adapter as any).callClient}>
            {adapter && (
              <CallAgentProvider callAgent={(adapter as any).callAgent}>
                {call ? (
                  <CallProvider call={call}>
                    <CallingComponents onCallEnded={() => onCallEnded()} />
                  </CallProvider>
                ) : <Lobby/>}
              </CallAgentProvider>
            )}
          </CallClientProvider>
        )}
        <Dialog
          hidden={hideDialog}
          onDismiss={toggleDismissDialog}
          dialogContentProps={dismissalDialogProps}
          modalProps={modalProps}
        >
          <div style={{ textAlign: "right" }}>
            <PrimaryButton
                onClick={() => {                  
                  window.location.href = 'https://educationstandards.nsw.edu.au/';
                }}
                text="Return to Home"
              />
          </div>          
        </Dialog>
      </FluentThemeProvider>
    </>
  )
}

const convertPageStateToString = (state: CallAdapterState): string => {
  switch (state.page) {
    case "accessDeniedTeamsMeeting":
      return "error"
    case "leftCall":
      return "end call"
    case "removedFromCall":
      return "end call"
    default:
      return `${state.page}`
  }
}
